import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Divider,
  Stepper,
  Step,
  StepLabel,
  Grid,
  Paper,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import {
  Visibility,
  ShoppingBag,
  Close,
  ContentCopy,
  ReceiptLong,
  Storefront,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../../../context/UserContext';
import { apiList, invokeApi, invokeGetApi } from '../../../services/apiServices';
import { InvoiceModal, PURPLE } from '../../common/InvoiceDocument';

const statusColor = (status) => {
  const s = String(status || '').toLowerCase();
  if (s.includes('deliver') || s.includes('paid') || s.includes('complete') || s.includes('success')) {
    return { bg: '#E8F5E9', color: '#2D6A4F' };
  }
  if (s.includes('cancel') || s.includes('failed') || s.includes('refund')) {
    return { bg: '#FFEBEE', color: '#D32F2F' };
  }
  return { bg: '#FFF3E0', color: '#F57C00' };
};

const OrderHistory = () => {
  const { user } = useUser();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [invoiceModalOrder, setInvoiceModalOrder] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }

    const fetchCustomerOrders = async () => {
      setLoading(true);
      try {
        const userEmail = (user?.email || '').toLowerCase().trim();
        const userId = user?.id || user?._id;
        const userPhone = (user?.phone || user?.phoneNumber || '').replace(/[^0-9]/g, '');

        let fetchedList = [];

        // 1. Try to fetch customer invoices
        try {
          const invRes = await invokeApi(apiList.getInvoices, { customer_email: userEmail, search: userEmail });
          if (invRes?.status === 200 || invRes?.data?.responseCode === '200') {
            const rawInvoices = invRes.data?.data?.invoices || invRes.data?.invoices || (Array.isArray(invRes.data) ? invRes.data : []);
            if (Array.isArray(rawInvoices)) {
              fetchedList = rawInvoices.filter((inv) => {
                const invEmail = (inv.customer_email || inv.email || '').toLowerCase().trim();
                return invEmail === userEmail;
              });
            }
          }
        } catch (e) {
          console.warn('Notice: Could not fetch customer invoices from API:', e);
        }

        // 2. Try to fetch from getAllProductOrder or getOrders
        if (fetchedList.length === 0) {
          try {
            const pRes = await invokeApi(apiList.getAllProductOrder, { userId });
            const pOrders = pRes?.data?.orders || pRes?.data?.productOrder;
            if (pOrders && Array.isArray(pOrders)) {
              fetchedList = pOrders.filter((p) => {
                const pEmail = (p.customer_email || p.email || '').toLowerCase().trim();
                const pUser = String(p.userId || p.user_id || '');
                return (userEmail && pEmail === userEmail) || (userId && pUser === String(userId));
              });
            }
          } catch (e) {}
        }

        if (fetchedList.length === 0) {
          try {
            const ordRes = await invokeGetApi(apiList.getOrders || '/getOrders');
            if (ordRes?.data?.orders && Array.isArray(ordRes.data.orders)) {
              fetchedList = ordRes.data.orders.filter((ord) => {
                const ordEmail = (ord.customer_email || ord.email || '').toLowerCase().trim();
                const ordPhone = (ord.customer_phone || ord.phone || '').replace(/[^0-9]/g, '');
                const ordUser = String(ord.userId || ord.user_id || '');
                return (
                  (userEmail && ordEmail === userEmail) ||
                  (userId && ordUser === String(userId)) ||
                  (userPhone && ordPhone && ordPhone.includes(userPhone))
                );
              });
            }
          } catch (e) {
            console.warn('Notice: Could not fetch customer orders from API:', e);
          }
        }

        // 3. Merge with customer local orders placed during checkout in current browser
        try {
          const localOrdersKey = `ar_customer_orders_${userEmail}`;
          const savedLocal = JSON.parse(localStorage.getItem(localOrdersKey) || localStorage.getItem('ar_customer_orders') || '[]');
          if (Array.isArray(savedLocal) && savedLocal.length > 0) {
            const existingIds = new Set(fetchedList.map((o) => o.id || o.invoice_number || o.order_number));
            savedLocal.forEach((loc) => {
              const locEmail = (loc.customer_email || loc.email || '').toLowerCase().trim();
              if (!locEmail || locEmail === userEmail) {
                const locId = loc.id || loc.invoice_number || loc.order_number;
                if (!existingIds.has(locId)) {
                  fetchedList.unshift(loc);
                }
              }
            });
          }
        } catch (e) {}

        // Format normalized orders
        const normalized = fetchedList.map((o, idx) => {
          const orderNumber = o.invoice_number || o.order_number || o.orderId || o.id || `ORD-${1000 + idx}`;
          const dateStr = o.date || (o.created_at ? new Date(o.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'Recent');
          const totalNum = parseFloat(o.total_amount || o.totalAmount || o.total || 0);
          const rawItems = o.items || o.order_items || [];
          const status = o.order_status || o.status || 'Completed';

          return {
            id: orderNumber,
            invoice_number: orderNumber,
            date: dateStr,
            total: `₹${totalNum.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
            total_amount: totalNum,
            status: status,
            payment_status: o.payment_status || 'Paid',
            items: Array.isArray(rawItems) && rawItems.length > 0 ? rawItems : [{ description: 'Software License Purchase', quantity: 1, unit_price: totalNum, total_price: totalNum, hsn_sac: '997331' }],
            deliveryAddress: o.shipping_address || o.deliveryAddress || o.address || 'Instant Digital Delivery via Registered Email',
            customer_name: o.customer_name || o.fullName || user.name || 'Valued Customer',
            email: o.customer_email || o.email || user.email,
            phone: o.customer_phone || o.phone || user.phone || 'N/A',
            paymentMethod: o.paymentMethod || 'Online Prepaid (Cashfree)',
            timeline: ['Order Placed', 'Payment Verified', 'License Key Generated', 'Delivered to Email'],
            activeStep: status.toLowerCase().includes('deliver') || status.toLowerCase().includes('complete') ? 4 : 2,
          };
        });

        setOrders(normalized);
      } catch (err) {
        console.error('Error fetching customer orders:', err);
        setOrders([]);
      } finally {
        setLoading(false);
      }
    };

    fetchCustomerOrders();
  }, [user]);

  const handleCopy = (id) => {
    navigator.clipboard.writeText(id);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (!user) {
    return (
      <Box sx={{ textAlign: 'center', py: 6 }}>
        <Typography variant="h6" color="text.secondary">Please sign in to view your orders.</Typography>
        <Button variant="contained" onClick={() => navigate('/login')} sx={{ mt: 2, bgcolor: '#0E6ADA' }}>
          Sign In
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <ShoppingBag sx={{ color: 'primary.main', fontSize: 30 }} />
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 800, color: '#1A237E' }}>
              My Orders & Invoices
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Orders and official tax invoices for {user.name || user.email}
            </Typography>
          </Box>
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <CircularProgress sx={{ color: '#0E6ADA', mb: 2 }} />
          <Typography variant="body2" color="text.secondary">Loading your orders...</Typography>
        </Box>
      ) : orders.length > 0 ? (
        <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #E2E8F0', borderRadius: 2.5 }}>
          <Table sx={{ minWidth: 650 }}>
            <TableHead>
              <TableRow sx={{ bgcolor: '#F8FAFC' }}>
                <TableCell sx={{ fontWeight: 700 }}>Order ID</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Total Amount</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {orders.map((order) => {
                const sc = statusColor(order.status);
                return (
                  <TableRow key={order.id} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                    <TableCell component="th" scope="row" sx={{ fontWeight: 700, color: '#1A237E' }}>
                      {order.id}
                    </TableCell>
                    <TableCell>{order.date}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{order.total}</TableCell>
                    <TableCell>
                      <Chip
                        label={order.status}
                        size="small"
                        sx={{ borderRadius: 1.5, fontWeight: 700, bgcolor: sc.bg, color: sc.color }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                        <Tooltip title="View Order Details">
                          <IconButton
                            size="small"
                            onClick={() => setSelectedOrder(order)}
                            sx={{ color: 'primary.main', '&:hover': { bgcolor: 'primary.50' } }}
                          >
                            <Visibility fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Download Tax Invoice">
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<ReceiptLong sx={{ fontSize: 16 }} />}
                            onClick={() => setInvoiceModalOrder(order)}
                            sx={{
                              borderColor: PURPLE,
                              color: PURPLE,
                              fontWeight: 700,
                              textTransform: 'none',
                              fontSize: '12px',
                              borderRadius: 1.5,
                              px: 1.5,
                              py: 0.4,
                              '&:hover': { borderColor: '#6454DB', bgcolor: '#F4F3FB' },
                            }}
                          >
                            Tax Invoice
                          </Button>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Paper
          elevation={0}
          sx={{
            textAlign: 'center',
            py: 8,
            px: 3,
            border: '1.5px dashed #CBD5E1',
            borderRadius: 3,
            bgcolor: '#F8FAFC'
          }}
        >
          <ShoppingBag sx={{ fontSize: 50, color: '#94A3B8', mb: 1.5 }} />
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#1E293B', mb: 0.5 }}>
            No Orders Found
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 450, mx: 'auto', mb: 3 }}>
            You haven't placed any software license orders yet. Browse genuine Microsoft product keys with instant email delivery!
          </Typography>
          <Button
            variant="contained"
            startIcon={<Storefront />}
            onClick={() => navigate('/shop')}
            sx={{
              bgcolor: '#0E6ADA',
              color: '#FFF',
              fontWeight: 700,
              borderRadius: 2,
              px: 3.5,
              py: 1.2,
              textTransform: 'none',
              boxShadow: '0 4px 14px rgba(14,106,218,0.3)',
              '&:hover': { bgcolor: '#0b54b0' }
            }}
          >
            Explore Software Shop
          </Button>
        </Paper>
      )}

      {/* ── Order Detail Dialog ── */}
      <Dialog
        open={!!selectedOrder}
        onClose={() => setSelectedOrder(null)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        {selectedOrder && (
          <>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1.5 }}>
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 800, color: '#1A237E' }}>
                    {selectedOrder.id}
                  </Typography>
                  <IconButton size="small" onClick={() => handleCopy(selectedOrder.id)} title="Copy">
                    <ContentCopy sx={{ fontSize: 16 }} />
                  </IconButton>
                  {copied && <Typography variant="caption" color="success.main">Copied!</Typography>}
                </Box>
                <Typography variant="caption" color="text.secondary">
                  Placed on {selectedOrder.date}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Chip
                  label={selectedOrder.status}
                  size="small"
                  sx={{
                    borderRadius: 1.5,
                    fontWeight: 700,
                    bgcolor: statusColor(selectedOrder.status).bg,
                    color: statusColor(selectedOrder.status).color,
                  }}
                />
                <IconButton size="small" onClick={() => setSelectedOrder(null)}>
                  <Close fontSize="small" />
                </IconButton>
              </Box>
            </DialogTitle>
            <Divider />
            <DialogContent sx={{ py: 3 }}>
              {/* Stepper */}
              <Box sx={{ mb: 4 }}>
                <Stepper activeStep={selectedOrder.activeStep} alternativeLabel>
                  {selectedOrder.timeline.map((label) => (
                    <Step key={label}>
                      <StepLabel>{label}</StepLabel>
                    </Step>
                  ))}
                </Stepper>
              </Box>

              {/* Items Table */}
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: '#1A237E' }}>
                Purchased Licenses
              </Typography>
              <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #E2E8F0', mb: 3 }}>
                <Table size="small">
                  <TableHead sx={{ bgcolor: '#F8FAFC' }}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700 }}>Description</TableCell>
                      <TableCell sx={{ fontWeight: 700 }} align="center">Qty</TableCell>
                      <TableCell sx={{ fontWeight: 700 }} align="right">Price</TableCell>
                      <TableCell sx={{ fontWeight: 700 }} align="right">Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedOrder.items.map((it, idx) => (
                      <TableRow key={idx}>
                        <TableCell sx={{ fontWeight: 600 }}>{it.description || it.product_name || it.name}</TableCell>
                        <TableCell align="center">{it.quantity || 1}</TableCell>
                        <TableCell align="right">₹{(it.unit_price || it.price || 0).toLocaleString('en-IN')}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>₹{(it.total_price || it.total || (it.unit_price || it.price || 0) * (it.quantity || 1)).toLocaleString('en-IN')}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Customer & Delivery Details */}
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', display: 'block', mb: 0.5 }}>
                      DELIVERY DETAILS
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{selectedOrder.customer_name}</Typography>
                    <Typography variant="body2" color="text.secondary">{selectedOrder.deliveryAddress}</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>Phone: {selectedOrder.phone}</Typography>
                    <Typography variant="body2" color="text.secondary">Email: {selectedOrder.email}</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', display: 'block', mb: 0.5 }}>
                      PAYMENT SUMMARY
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{selectedOrder.paymentMethod}</Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                      <Typography variant="body2" color="text.secondary">Total Paid:</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 800, color: '#1A237E' }}>{selectedOrder.total}</Typography>
                    </Box>
                  </Paper>
                </Grid>
              </Grid>
            </DialogContent>
            <Divider />
            <DialogActions sx={{ p: 2, gap: 1 }}>
              <Button
                variant="outlined"
                startIcon={<ReceiptLong />}
                onClick={() => {
                  setInvoiceModalOrder(selectedOrder);
                }}
                sx={{
                  borderColor: PURPLE,
                  color: PURPLE,
                  fontWeight: 700,
                  textTransform: 'none',
                  borderRadius: 2,
                  '&:hover': { borderColor: '#6454DB', bgcolor: '#F4F3FB' },
                }}
              >
                View Official Tax Invoice
              </Button>
              <Button
                variant="contained"
                onClick={() => setSelectedOrder(null)}
                sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700, bgcolor: '#1A237E' }}
              >
                Close
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* ── Tax Invoice Document Modal ── */}
      {invoiceModalOrder && (
        <InvoiceModal
          open={!!invoiceModalOrder}
          onClose={() => setInvoiceModalOrder(null)}
          invoice={{
            invoice_number: invoiceModalOrder.id,
            customer_name: invoiceModalOrder.customer_name,
            customer_email: invoiceModalOrder.email,
            customer_number: invoiceModalOrder.phone,
            address: invoiceModalOrder.deliveryAddress,
            due_date: invoiceModalOrder.date,
            total_amount: invoiceModalOrder.total_amount,
            received: invoiceModalOrder.total_amount,
            balance: 0,
            status: invoiceModalOrder.status,
            payment_status: invoiceModalOrder.payment_status,
            items: invoiceModalOrder.items.map((it, idx) => ({
              index: idx + 1,
              description: it.description || it.product_name || it.name,
              hsn_sac: it.hsn_sac || '997331',
              quantity: it.quantity || 1,
              unit: it.unit || 'Pcs',
              unit_price: it.unit_price || it.price || 0,
              total_price: it.total_price || (it.unit_price || it.price || 0) * (it.quantity || 1)
            }))
          }}
        />
      )}
    </Box>
  );
};

export default OrderHistory;
